// `EmailChangeFlow` — US-188 gap-design implementation.
//
// Flow (proposed inline, surfaced for review in the agent's report):
//
//   1. User clicks "Modifier l’adresse" on the Profil row.
//   2. Inline form appears with a single email input + submit.
//   3. On submit, we call Better Auth's `/auth/change-email` endpoint
//      (`useChangeEmail`). BA's `sendChangeEmailConfirmation` callback
//      sends a confirmation email to the OLD address.
//   4. The Profil row flips into a pending banner: "{old} → {new} en
//      attente de vérification" with "Renvoyer" / "Annuler" actions.
//   5. User clicks the link in the OLD-address email → BA re-issues a
//      verification token and emails the NEW address via
//      `emailVerification.sendVerificationEmail`.
//   6. User clicks the link in the NEW-address email → BA finalises the
//      swap, fires the `email-changed` notice back to the OLD address
//      (`sendEmailChanged`), and the new address becomes the login
//      identifier. The session cookie is rotated for the new identity.
//
// Pending state is held in component state — the BA verifications table
// keeps the actual server-side truth, but until either email lands the
// row UI shows the friendly target so the user knows what to expect.
// "Renvoyer" calls `/auth/change-email` again with the same `newEmail`
// so BA re-issues a token (and re-sends the OLD-address email).
// "Annuler" simply clears the local pending state — BA tokens expire
// naturally.

import { useState } from 'react';

import { ApiError, useChangeEmail } from '@barel/api-client/react';
import { useT } from '@barel/i18n';
import { Banner, Button, Field, IconButton, Input, toast } from '@barel/ui';
import { Mail, X } from 'lucide-react';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

const SCHEMA = z.object({
  newEmail: z.string().trim().min(3).max(254).email(),
});

type FormValues = z.infer<typeof SCHEMA>;

export interface EmailChangeFlowProps {
  /** Current verified email (read-only display). */
  currentEmail: string;
}

interface PendingState {
  newEmail: string;
}

export function EmailChangeFlow({ currentEmail }: EmailChangeFlowProps) {
  const t = useT('settings');
  const tCommon = useT('common');
  const changeEmail = useChangeEmail();
  const [editing, setEditing] = useState<boolean>(false);
  const [pending, setPending] = useState<PendingState | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(SCHEMA),
    mode: 'onBlur',
    defaultValues: { newEmail: '' },
  });

  const onSubmit: SubmitHandler<FormValues> = async (values) => {
    setSubmitError(null);
    if (values.newEmail.toLowerCase() === currentEmail.toLowerCase()) {
      setSubmitError(t('profile.emailSameError'));
      return;
    }
    try {
      await changeEmail.mutateAsync({ newEmail: values.newEmail });
      setPending({ newEmail: values.newEmail });
      setEditing(false);
      form.reset();
      toast.success({ title: t('profile.emailNewToast') });
    } catch (err) {
      if (ApiError.is(err) && err.code === 'validation.failed') {
        setSubmitError(t('profile.emailSameError'));
      } else {
        setSubmitError(t('profile.emailNewError'));
      }
    }
  };

  const handleResend = async (): Promise<void> => {
    if (!pending) return;
    try {
      await changeEmail.mutateAsync({ newEmail: pending.newEmail });
      toast.success({ title: t('profile.emailNewToast') });
    } catch {
      toast.error({ title: t('profile.emailNewError') });
    }
  };

  return (
    <div data-component="EmailChangeFlow" className="flex flex-col gap-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-1 flex-col gap-0.5">
          <span className="text-sm font-medium text-primary">{t('profile.emailLabel')}</span>
          <span className="text-xs text-tertiary">{t('profile.emailHint')}</span>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span data-testid="settings-current-email" className="text-sm text-secondary">
            {currentEmail}
          </span>
          {!editing && !pending ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setEditing(true)}
              data-testid="settings-email-edit"
            >
              {t('profile.emailEdit')}
            </Button>
          ) : null}
        </div>
      </div>

      {pending ? (
        <Banner
          tone="info"
          data-testid="settings-email-pending"
          icon={<Mail size={16} />}
          title={t('profile.emailPending', { old: currentEmail, next: pending.newEmail })}
        >
          <div className="flex flex-col gap-2">
            <p className="text-sm text-secondary">{t('profile.emailPendingHint')}</p>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                size="sm"
                variant="secondary"
                loading={changeEmail.isPending}
                onClick={() => {
                  void handleResend();
                }}
                data-testid="settings-email-resend"
              >
                {t('profile.emailResend')}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => setPending(null)}
                data-testid="settings-email-cancel"
              >
                {t('profile.emailCancel')}
              </Button>
            </div>
          </div>
        </Banner>
      ) : null}

      {editing ? (
        <form
          onSubmit={(e) => {
            void form.handleSubmit(onSubmit)(e);
          }}
          className="flex flex-col gap-3"
          data-testid="settings-email-form"
          noValidate
        >
          <Field invalid={!!form.formState.errors.newEmail || !!submitError} required>
            <Field.Label>{t('profile.emailNewLabel')}</Field.Label>
            <Field.Control>
              <Input
                {...form.register('newEmail')}
                type="email"
                autoComplete="email"
                inputMode="email"
                placeholder={t('profile.emailNewPlaceholder')}
                data-testid="settings-email-new"
                iconRight={
                  <IconButton
                    type="button"
                    variant="ghost"
                    size="sm"
                    aria-label={tCommon('actions.cancel')}
                    onClick={() => {
                      setEditing(false);
                      setSubmitError(null);
                      form.reset();
                    }}
                    icon={<X size={14} />}
                  />
                }
              />
            </Field.Control>
            {submitError ? <Field.Error>{submitError}</Field.Error> : null}
          </Field>
          <div className="flex items-center gap-2">
            <Button
              type="submit"
              size="sm"
              variant="primary"
              loading={form.formState.isSubmitting || changeEmail.isPending}
              data-testid="settings-email-submit"
            >
              {t('profile.emailNewSubmit')}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                setEditing(false);
                setSubmitError(null);
                form.reset();
              }}
            >
              {tCommon('actions.cancel')}
            </Button>
          </div>
        </form>
      ) : null}
    </div>
  );
}
